import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  FaTimes,
  FaTruck,
  FaInfoCircle,
  FaCheckCircle,
  FaTrash,
  FaCheckDouble,
  FaQrcode,
  FaExclamationTriangle,
  FaWarehouse,
  FaLayerGroup,
  FaPlus,
  FaFileInvoice,
  FaTrashAlt
} from "react-icons/fa";

// Added domainAccounting import
import { domain,
  domainAccounting 
} from "../../../../security";
import { 
  selectUserID, 
  selectFullName 
} from "../../../../redux/IchthusSlice";
import SearchableDropdown from "../../../../UI/common/SearchableDropdown";
import SelectedSerialModal from "../../POS/PosModule/SelectedSerialModal";
import { AverageCostModal } from "./AllDeliveryOrders";
import { serviceCategories } from "../../Inventory/InventoryModule/AllInventory";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const AddDeliveryOrder = ({ onClose, onSuccess }) => {
  const currentUserId = useSelector(selectUserID);
  const currentUserName = useSelector(selectFullName);

  // ── Form state ──
  const [formData, setFormData] = useState({
    deliveryDate: new Date().toISOString().slice(0, 16),
    salesOrderId: "",
    notes: "",
    preparedBy: "",
    deliveryLines: [],
    deliveryOrderMainDetailed: [],
    customerId: null,
  });

  const [approvedSOs, setApprovedSOs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedSO, setSelectedSO] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingSyncData, setPendingSyncData] = useState(null);

  // ── Modal State ──
  const [activeSerialProduct, setActiveSerialProduct] = useState(null);

  const inputStyles =
    "block w-full p-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white";
  const labelStyles = "block text-sm font-semibold text-gray-700 mb-1";

  const fetchDataCustomer = useCallback(async () => {
    try {
      const response = await axios.get(`${domain}/api/Customers` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return []
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const _customers = await fetchDataCustomer();
      setCustomers(_customers)
    }

    fetchData();
  }, [fetchDataCustomer])

  const [salesOrdersByCustomer, setSalesOrdersByCustomer] = useState([])

  const fetchDataSOByCustomer = useCallback(async (customerId) => {
    try {
      const response = await axios.get(`${domain}/api/SalesOrders/customer/${customerId}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return []
    }
  }, []);

  useEffect(() => {
    if(formData.customerId){
      const fetch = async () => {
        setIsLoading(true)
        const _data = await fetchDataSOByCustomer(formData.customerId)
        setSalesOrdersByCustomer(_data)
        setIsLoading(false)
      }

      fetch();
    }
  }, [fetchDataSOByCustomer, formData.customerId])

  // ── Auto-fill preparedBy ──
  useEffect(() => {
    if (currentUserName)
      setFormData((prev) => ({ ...prev, preparedBy: currentUserName }));
  }, [currentUserName]);

  // ── Fetch approved SOs ──
  useEffect(() => {
    const fetchSOs = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${domain}/api/SalesOrders`);
        const approved = (res.data || []).filter(
          (so) => (so.status?.toLowerCase() === "approved" || so.status?.toLowerCase() === "partial"),
        );
        setApprovedSOs(approved);
      } catch {
        toast.error("Failed to load Sales Orders.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchSOs();
  }, []);

  // ── When an SO is selected, load per-line delivery progress + stock ──
  useEffect(() => {
    if (!formData.salesOrderId || approvedSOs.length === 0) {
      setSelectedSO(null);
      setFormData((prev) => ({ ...prev, deliveryLines: [] }));
      return;
    }

    const targetId = parseInt(formData.salesOrderId);
    const so = approvedSOs.find((s) => s.id === targetId);
    if (!so) return;

    const loadProgress = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${domain}/api/DeliveryOrders/bySo/${targetId}`,
        );
        const lineProgress = res.data?.lineProgress || [];

        const lines = lineProgress.map((lp) => ({
          salesOrderLineItemId: lp.id,
          productId: lp.productId,
          brandName: lp.brandName,
          categoryId: lp.categoryId,
          categoryName: lp.categoryName,
          productName: lp.productName,
          productCode: lp.productCode,
          hasSerial: lp.hasSerial,
          unitPrice: lp.unitPrice || 0,
          orderedQuantity: lp.orderedQuantity,
          deliveredQuantity: lp.deliveredQuantity || 0,
          remainingQuantity:
            lp.remainingQuantity > 0 ? lp.remainingQuantity : 0,
          availableStock: lp.availableStock ?? 0,
          canDeliver: lp.canDeliver ?? 0,
          quantityToDeliver: lp.canDeliver > 0 ? lp.canDeliver : 0,
          serialNumbers: [],
        }));

        setSelectedSO(so);
        setFormData((prev) => ({ ...prev, deliveryLines: lines }));
      } catch {
        toast.error("Failed to fetch order progress data.");
        setFormData((prev) => ({ ...prev, deliveryLines: [] }));
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [formData.salesOrderId, approvedSOs]);

  // ── Handlers ──
  const handleHeaderChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSOSelect = (value) => {
    const cleanValue =
      typeof value === "object" && value !== null
        ? value.value || value.id
        : value;
        
    setFormData({ ...formData, salesOrderId: cleanValue });
  };

  const handleCustomerSelect = (value) => {
    const cleanValue =
      typeof value === "object" && value !== null
        ? value.value || value.id
        : value;
    setFormData({ ...formData, customerId: cleanValue });
  };

  // const handleQuantityChange = (index, value) => {
  //   const lines = [...formData.deliveryLines];
  //   const line = lines[index];

  //   const max = line.canDeliver;
  //   let newQty = parseFloat(value) || 0;

  //   if (newQty < 0) newQty = 0;
  //   if (newQty > max) {
  //     if (line.availableStock < line.remainingQuantity) {
  //       toast.warn(
  //         `Only ${line.availableStock} unit(s) available in stock for ${line.productName}.`,
  //       );
  //     } else {
  //       toast.warn(`Cannot deliver more than ${max} for ${line.productName}.`);
  //     }
  //     newQty = max;
  //   }

  //   lines[index] = {
  //     ...line,
  //     quantityToDeliver: newQty,
  //     serialNumbers: [],
  //   };
  //   setFormData({ ...formData, deliveryLines: lines });
  // };

  const handleQuantityChange = (soIndex, itemIndex, value) => {
    const updatedMain = [...formData.deliveryOrderMainDetailed];
    const line = updatedMain[soIndex].deliveryOrderMainDetailedItem[itemIndex];

    const max = line.canDeliver;
    let newQty = parseFloat(value) || 0;

    if (newQty < 0) newQty = 0;
    if (newQty > max) {
      if (line.availableStock < line.remainingQuantity) {
        toast.warn(
          `Only ${line.availableStock} unit(s) available in stock for ${line.productName}.`
        );
      } else {
        toast.warn(`Cannot deliver more than ${max} for ${line.productName}.`);
      }
      newQty = max;
    }

    // Update the nested item immutably
    const updatedItems = [...updatedMain[soIndex].deliveryOrderMainDetailedItem];
    updatedItems[itemIndex] = {
      ...line,
      quantityToDeliver: newQty,
      serialNumbers: [], // Reset serials if quantity changes
    };

    updatedMain[soIndex] = {
      ...updatedMain[soIndex],
      deliveryOrderMainDetailedItem: updatedItems,
    };

    setFormData({ ...formData, deliveryOrderMainDetailed: updatedMain });
  };

  const handleRevertLine = (index) => {
    const lines = [...formData.deliveryLines];
    lines[index] = { ...lines[index], quantityToDeliver: 0, serialNumbers: [] };
    setFormData({ ...formData, deliveryLines: lines });
  };

  const handleSetMaxAll = () => {
    const lines = formData.deliveryLines.map((l) => ({
      ...l,
      quantityToDeliver: l.canDeliver,
      serialNumbers: [],
    }));
    setFormData({ ...formData, deliveryLines: lines });
  };

  const handleSaveSerials = (lineIndex, serialIds) => {
    const lines = [...formData.deliveryLines];
    lines[lineIndex].serialNumbers = serialIds;
    setFormData({ ...formData, deliveryLines: lines });
    setActiveSerialProduct(null);
  };

  /* const fetchSOById = useCallback(async (id) => {
    try {
      const response = await axios.get(`${domain}/api/SalesOrders/${id}` , {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []); */

  /* const handleSubmit = async () => {
    if (!formData.salesOrderId)
      return toast.error("Please select a Sales Order.");

    const validLines = formData.deliveryLines.filter(
      (l) => l.quantityToDeliver > 0,
    );
    if (validLines.length === 0) {
      return toast.warn("Enter a delivery quantity for at least one item.");
    }

    const overStock = validLines.filter(
      (l) => l.quantityToDeliver > l.availableStock,
    );
    if (overStock.length > 0) {
      return toast.error(
        `Insufficient stock for "${overStock[0].productName}". Available: ${overStock[0].availableStock}.`,
      );
    }

    const invalidSerials = validLines.filter(
      (l) => l.hasSerial && l.serialNumbers.length !== l.quantityToDeliver,
    );
    if (invalidSerials.length > 0) {
      return toast.error(
        `Please pick exactly ${invalidSerials[0].quantityToDeliver} serial(s) for ${invalidSerials[0].productName}.`,
      );
    }

    setIsLoading(true);
    try {
      const payload = {
        salesOrderId: parseInt(formData.salesOrderId),
        deliveryDate: new Date(formData.deliveryDate).toISOString(),
        notes: formData.notes,
        items: validLines.map((l) => ({
          salesOrderLineItemId: l.salesOrderLineItemId,
          quantityDelivered: l.quantityToDeliver,
          serialNumberIds: l.hasSerial ? l.serialNumbers : [],
        })),
      };

      console.log('Final bulkData:', JSON.stringify(payload, null, 2));

      const res = await axios.post(`${domain}/api/DeliveryOrders`, payload);

      // ── AUTO-SYNC DO TO ACCOUNTING ──
      try {
        const estimatedTotalValue = validLines.reduce(
          (sum, l) => sum + (l.quantityToDeliver || 0) * (l.unitPrice || 0),
          0,
        );

        const doNumberStr =
          res.data?.doNumber ||
          res.data?.data?.deliveryOrderNumber ||
          `DO-${Date.now()}`;

        const _so = await fetchSOById(parseInt(formData.salesOrderId) || 0)
        console.log({res})
        console.log({validLines})
        console.log({estimatedTotalValue})

        const doAccountingPayload = [
          {
            deliveryOrderDetails: validLines.map((l) => {
              const subTotal = (l.quantityToDeliver || 0) * (l.unitPrice || 0);
              return {
                id: 0,
                deliveryOrderHeaderId: 0,
                itemCode: l.productCode || l.productName || "Unknown",
                orderedQty: l.orderedQuantity || 0,
                receivedQty: l.quantityToDeliver || 0,
                remainingQty: Math.max(
                  0,
                  (l.remainingQuantity || 0) - (l.quantityToDeliver || 0),
                ),
                unitPrice: l.unitPrice || 0,
                subTotal: subTotal,
                taxationCodeId: 0,
                totalDiscount: 0,
                taxAmount: 0,
                totalAmount: subTotal,
              };
            }),
            doNumber: doNumberStr,
            salesOrderSONumber: _so.salesOrderNumber,
            amount: estimatedTotalValue,
            doDate: new Date(formData.deliveryDate).toISOString(),
            uploadedFile: "",
            notes: formData.notes || "",
            status: "Closed",
            createdDate: new Date().toISOString(),
            createdBy: currentUserId || 0,
          },
        ];

        await axios.post(
          `${domainAccounting}/api/DeliveryOrder/bulk`,
          doAccountingPayload,
        );
        console.log(`Auto-synced DO ${doNumberStr} to Accounting`);
      } catch (accErr) {
        console.error("Failed to auto-sync DO to Accounting:", accErr);
      }
      // ─────────────────────────────────

      toast.success(
        res.data?.message || "Delivery Order created successfully!",
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to create Delivery Order.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }; */

  const [showAverageCostConfig, setShowAverageCostConfig] = useState(false)
  const [itemsWithoutAverageCost, setItemsWithoutAverageCost] = useState(false)
  const openAverageCostConfig = (_data) => {
    setShowAverageCostConfig(true);
    setItemsWithoutAverageCost(_data)
  }
  const closeAverageCostConfig = () => {
    setShowAverageCostConfig(false);
    setPendingSyncData(null); // Clear pending data if user cancels
  }

  // const handleAccountingSubmit = async (_data) => {
  //   setIsLoading(true);

  //   const amount = _data.deliveryOrderMainDetailed?.reduce(
  //       (sum, item) => sum + (Number(item.grandTotal) || 0),
  //       0
  //     ) || 0;

  //   const finalData = [
  //     {
  //       deliveryOrderDetails: _data.deliveryOrderMainDetailed?.map((item) => ({
  //         salesOrderSONumber: item.salesOrderNumber,

  //         deliveryOrderDetailLines: item.deliveryOrderMainDetailedItem?.map((line) => ({
  //           itemCode: line.product?.itemCode,
  //           orderedQty: line.salesOrderItem?.quantity,
  //           receivedQty: line.quantity,
  //           remainingQty: line.quantityRemaining ?? 0,
  //           unitPrice: line.unitPrice,
  //           subTotal: line.lineTotal,
  //           taxationCodeId: 0,
  //           totalDiscount: 0,
  //           taxAmount: 0,
  //           totalAmount: line.lineTotal
  //         }))
  //       })),

  //       doNumber: _data.deliveryOrderNumber,
  //       customerId: _data.customer?.customerCode,
  //       amount: amount,
  //       doDate: _data.deliveryDate,
  //       uploadedFile: "",
  //       notes: _data?.notes,
  //       status: "Open",
  //       createdDate: new Date().toISOString(),
  //       createdBy: currentUserId || 0,
  //     }
  //   ];

  //   try {
  //     await axios.post(
  //       `${domainAccounting}/api/DeliveryOrder/bulk`,
  //       finalData,
  //     );

  //   } catch (error) {
  //     console.error(error);
  //   }
  // };
  // const handleSubmit = async () => {
  //   setIsLoading(true);

  //   // --- 1. Validate Header ---
  //   if (formData.deliveryOrderMainDetailed.length <= 0) {
  //     toast.error("Please select a valid Sales Order.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   const hasAtLeastOneItem = formData.deliveryOrderMainDetailed.some(item => 
  //     item.deliveryOrderMainDetailedItem.some(line => line.quantity > 0)
  //   );

  //   if (!hasAtLeastOneItem) {
  //     toast.error("Please input at least 1 item to deliver.");
  //     setIsLoading(false);
  //     return;
  //   }

  //   const payload = {
  //     ...formData,
  //     // Map through Sales Orders
  //     deliveryOrderMainDetailed: formData.deliveryOrderMainDetailed.map((so) => ({
  //       ...so,
  //       // Map through Items and update the quantity field
  //       deliveryOrderMainDetailedItem: so.deliveryOrderMainDetailedItem
  //         .filter((item) => item.quantityToDeliver > 0) // Optional: remove items with 0 qty
  //         .map((item) => ({
  //           ...item,
  //           quantity: item.quantityToDeliver, // Override quantity with the delivery amount
  //         })),
  //     })).filter(so => so.deliveryOrderMainDetailedItem.length > 0), // Optional: remove SOs with no items
  //   };

  //   try {
  //     const res = await axios.post(`${domain}/api/DeliveryOrderMain`, payload);

  //     await handleAccountingSubmit(res.data)
  //     toast.success(
  //       res.data?.message || "Delivery Order created successfully!",
  //     );
  //     onSuccess?.();
  //     onClose();
  //   } catch (error) {
  //     console.error(error);
  //     const msg =
  //       error.response?.data?.message || "Failed to create Delivery Order.";
  //     const detailedMsg = error.response?.data?.error || msg;
  //     toast.error(detailedMsg);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  
  const handleSaveCostsAndContinue = async (updatedCosts) => {
    let transaction = [];

    formData?.deliveryOrderMainDetailed?.forEach(order => {
      order?.deliveryOrderMainDetailedItem?.forEach(ref => {
        if(ref?.bomComponents?.length === 0){
          const mainDataUpdated = updatedCosts?.find(
            i => i.itemCode === ref.productCode
          );

          const _mainQuantity = (ref.quantity || 0);

          const _mainAverageCost =
              mainDataUpdated?.averageCost || 0;

            const _mainInventoryValue =
              _mainAverageCost * _mainQuantity;

          transaction.push({
            referenceNumber: order?.salesOrderNumber,
            itemCode: ref.productCode,
            quantity: _mainQuantity,
            inventoryValue: _mainInventoryValue,
            averageCost: 0
          });
        }
        
        ref?.bomComponents?.forEach(comp => {

          const dataUpdated = updatedCosts?.find(
            i => i.itemCode === comp.itemCode
          );

          if (dataUpdated) {

            const _quantity =
              (comp.quantity || 0) *
              (ref.quantity || 0);

            const _averageCost =
              dataUpdated.averageCost || 0;

            const _inventoryValue =
              _averageCost * _quantity;

            transaction.push({
              referenceNumber: order?.salesOrderNumber,
              itemCode: comp.itemCode,
              quantity: _quantity,
              inventoryValue: _inventoryValue,
              averageCost: 0
            });
          }
        });
      });
    });

    try {
      setIsLoading(true);

      // ==========================================
      // 1. SAVE INITIAL AVERAGE COST
      // ==========================================

      try {

        await axios.post(
          `${domainAccounting}/api/Item/update-average-costs`,
          transaction
        );

        toast.success("Average costs updated.");

      } catch (error) {

        // Accounting failed
        // BUT POS MUST CONTINUE

        console.error(
          "Accounting average cost update failed:",
          error
        );

        toast.warning(
          "Average cost was not synced to Accounting."
        );
      }


      // ==========================================
      // 2. CLOSE AVERAGE COST MODAL
      // ==========================================

      closeAverageCostConfig();


      // ==========================================
      // 3. TRY ACCOUNTING DO SYNC
      // ==========================================

      if (pendingSyncData) {

        await proceedWithSync(pendingSyncData);

      }


      // ==========================================
      // 4. ALWAYS COMPLETE POS
      // ==========================================

      setPendingSyncData(null);

      onSuccess?.();

      onClose();


    } catch (error) {

      // This should only catch unexpected frontend
      // errors, not Accounting errors.

      console.error(
        "Unexpected error after POS save:",
        error
      );

      // STILL CLOSE POS
      onSuccess?.();
      onClose();

    } finally {

      setIsLoading(false);

    }
  };

  const getItemsWithoutAverageCost = async (itemCodes) => {
    try {
      const response = await axios.post(
        `${domainAccounting}/api/Item/check-no-average-cost-items`,
        itemCodes,
      );

      return response.data

    } catch (error) {
      console.error(error);
      return []
    }
  };

  const proceedWithSync = async (dataToSync) => {
    try {
      await axios.post(
        `${domainAccounting}/api/DeliveryOrder/bulk`,
        dataToSync
      );

      toast.success(
        `Successfully pushed ${dataToSync?.length || 0} to Accounting!`
      );

      return true;

    } catch (error) {
      console.error("Accounting sync failed:", error);

      toast.warning(
        "Delivery Order saved, but Accounting sync failed."
      );

      return false;
    }
  };

  const handleAccountingSubmit = async (_data) => {
    try {
      const amount =
        _data.deliveryOrderMainDetailed?.reduce(
          (sum, item) => sum + (Number(item.grandTotal) || 0),
          0
        ) || 0;

      let itemCodes = [];

      const finalData = [
        {
          deliveryOrderDetails:
            _data.deliveryOrderMainDetailed?.map((item) => ({
              salesOrderSONumber: item.salesOrderNumber,

              deliveryOrderDetailLines:
                item.deliveryOrderMainDetailedItem?.map((line) => {
                  itemCodes.push(line.product?.itemCode);

                  return {
                    itemCode: line.product?.itemCode,
                    orderedQty: line.salesOrderItem?.quantity,
                    receivedQty: line.quantity,
                    remainingQty: line.quantityRemaining ?? 0,
                    unitPrice: line.unitPrice,
                    subTotal: line.lineTotal,
                    taxationCodeId: 0,
                    totalDiscount: line?.totalDiscount,
                    taxAmount: line?.taxAmount,
                    totalAmount: line?.totalAmount,
                  };
                }),
            })),

          doNumber: _data.deliveryOrderNumber,
          customerId: _data.customer?.customerCode,
          amount: amount,
          doDate: _data.deliveryDate,
          uploadedFile: "",
          notes: _data?.notes,
          status: "Open",
          createdDate: new Date().toISOString(),
          createdBy: currentUserId || 0,
        },
      ];

      const uniqueItemCodes = [...new Set(itemCodes)];

      const itemsWithoutAverageCost =
        await getItemsWithoutAverageCost(uniqueItemCodes);

      // ==========================================
      // MISSING AVERAGE COST
      // ==========================================
      if (itemsWithoutAverageCost.length > 0) {

        setPendingSyncData(finalData);
        setItemsWithoutAverageCost(itemsWithoutAverageCost);

        openAverageCostConfig(itemsWithoutAverageCost);

        // Tell handleSubmit to STOP here
        return true;
      }

      // ==========================================
      // NO MISSING COST
      // ==========================================

      // Accounting failure will NOT throw
      await proceedWithSync(finalData);

      return false;

    } catch (error) {
      // Even if checking Accounting fails,
      // POS should continue.
      console.error(
        "Accounting preparation failed:",
        error
      );

      return false;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (formData.deliveryOrderMainDetailed.length <= 0) {
      toast.error("Please select a valid Sales Order.");
      setIsLoading(false);
      return;
    }

    const hasAtLeastOneItem =
      formData.deliveryOrderMainDetailed.some(item =>
        item.deliveryOrderMainDetailedItem.some(
          line => line.quantity > 0 || serviceCategories.includes(line.categoryId)
        )
      );

    if (!hasAtLeastOneItem) {
      toast.error("Please input at least 1 item to deliver.");
      setIsLoading(false);
      return;
    }

    const payload = {
      ...formData,

      deliveryOrderMainDetailed:
        formData.deliveryOrderMainDetailed
          .map((so) => ({
            ...so,

            deliveryOrderMainDetailedItem:
              so.deliveryOrderMainDetailedItem
                .filter(item => item.quantityToDeliver > 0 || serviceCategories.includes(item.categoryId))
                .map(item => ({
                  ...item,
                  quantity: !serviceCategories.includes(item.categoryId) ? item.quantityToDeliver : item.orderedQuantity,
                })),
          }))
          .filter(
            so => so.deliveryOrderMainDetailedItem.length > 0
          ),
    };

    try {
      const res = await axios.post(
        `${domain}/api/DeliveryOrderMain`,
        payload
      );

      // Returns true when Average Cost modal was opened
      const waitingForAverageCost =
        await handleAccountingSubmit(res.data);

      // IMPORTANT:
      // Do NOT close AddDeliveryOrder if the modal is being shown
      if (waitingForAverageCost) {
        return;
      }

      toast.success(
        res.data?.message ||
        "Delivery Order created successfully!"
      );

      onSuccess?.();
      onClose();

    } catch (error) {
      console.error(error);

      const msg =
        error.response?.data?.message ||
        "Failed to create Delivery Order.";

      const detailedMsg =
        error.response?.data?.error || msg;

      toast.error(detailedMsg);

    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived values ──
  const soDropdownOptions = useMemo(
    () =>
      approvedSOs.map((so) => ({
        ...so,
        displayLabel: `${so.salesOrderNumber} | ${so.customerName || "N/A"} | ${formatCurrency(so.totalAmount)}`,
      })),
    [approvedSOs],
  );

  const totalUnitsToDeliver = formData.deliveryLines.reduce(
    (sum, l) => sum + (l.quantityToDeliver || 0),
    0,
  );

  const estimatedTotal = formData.deliveryLines.reduce(
    (sum, l) => sum + (l.quantityToDeliver || 0) * (l.unitPrice || 0),
    0,
  );

  const hasPartialLines = formData.deliveryLines.some(
    (l) => l.quantityToDeliver > 0 && l.quantityToDeliver < l.canDeliver,
  );

  const stockShortageCount = formData.deliveryLines.filter(
    (l) => l.remainingQuantity > 0 && l.availableStock === 0,
  ).length;

  const allExcludedSerials = formData.deliveryLines.flatMap(
    (l) => l.serialNumbers || [],
  );

  const handleAddSO = async () => {
    if (!selectedSO) return;
    
    setIsLoading(true);

    const targetId = parseInt(formData.salesOrderId);
    const so = approvedSOs.find((s) => s.id === targetId);
    if (!so) return;
    
    try {
      const res = await axios.get(
        `${domain}/api/DeliveryOrders/bySo/${targetId}`,
      );
      const lineProgress = res.data?.lineProgress || [];

      const lines = lineProgress.map((lp) => ({
        productId: lp.productId,
        categoryId: lp.categoryId,
        categoryName: lp.categoryName,
        brandName: lp.brandName,
        productName: lp.productName,
        productCode: lp.productCode,
        hasSerial: lp.hasSerial,
        unitPrice: lp.unitPrice || 0,
        orderedQuantity: lp.orderedQuantity,
        deliveredQuantity: lp.deliveredQuantity || 0,
        remainingQuantity:
          lp.remainingQuantity > 0 ? lp.remainingQuantity : 0,
        availableStock: lp.availableStock ?? 0,
        canDeliver: lp.canDeliver ?? 0,
        quantityToDeliver: lp.canDeliver > 0 ? lp.canDeliver : 0,
        serialNumbers: [],

        //jv added
        salesOrderItemId: lp.id,
        relatedLineItemId: lp.id,
        quantity: lp.canDeliver > 0 ? lp.canDeliver : 0,
        lineTotal: (lp.unitPrice || 0) * (lp.canDeliver > 0 ? lp.canDeliver : 0),
        uomId: lp.uomId,
        bomComponents: lp.bomComponents
      }));

      // setFormData((prev) => ({ ...prev, deliveryLines: lines }));

      // 1. Add to the "Added POs" list
      setFormData({
        ...formData,
        deliveryOrderMainDetailed: [
          ...formData.deliveryOrderMainDetailed,
          {
            salesOrderId: selectedSO.id,
            salesOrderNumber: selectedSO.displayLabel || selectedSO.salesOrderNumber,
            salesOrders: selectedSO, // Stored for restoration if removed
            grandTotal: selectedSO?.totalAmount ?? 0,
            
            // Map the line items and inject the new key + initial values
            // deliveryOrderMainDetailedItem: selectedSO.salesOrderItems?.filter(i => i.remainingQty > 0)?.map((line) => ({
            //   ...line,
            //   salesOrderItemId: line.id, // Setting the key as requested
            //   quantity: line.remainingQty,              // Initializing receiving qty
            // })),
            deliveryOrderMainDetailedItem: lines,
          },
        ],
      });

      // 2. Remove from the available dropdown options
      setSalesOrdersByCustomer((prev) => 
        prev.filter((item) => item.id !== selectedSO.id)
      );

      // 3. Reset selection
      setSelectedSO(null);
      // Also clear the specific field in formData used by the dropdown if necessary
      setFormData(prev => ({ ...prev, salesOrderId: "" }));

    } catch {
      toast.error("Failed to fetch order progress data.");
    } finally {
      setIsLoading(false)
    }
  };

  const handleRemoveSO = (id) => {
    // 1. Find the full PO object from our selected list before removing it
    const itemToRestore = formData.deliveryOrderMainDetailed.find(
      (item) => item.salesOrderId === id
    );

    if (!itemToRestore) return;

    // 2. Remove from the "Added POs" list
    setFormData({
      ...formData,
      deliveryOrderMainDetailed: formData.deliveryOrderMainDetailed.filter(
        (item) => item.salesOrderId !== id
      ),
    });

    // 3. Add the PO object back into the dropdown options
    setSalesOrdersByCustomer((prev) => [...prev, 
      {...itemToRestore.salesOrders, 
        displayLabel: `${itemToRestore.salesOrders?.salesOrderNumber}`
      }
    ]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 p-4 backdrop-blur-sm">
      {/* SERIAL PICKER MODAL */}
      {activeSerialProduct && (
        <SelectedSerialModal
          product={activeSerialProduct}
          excludedSerialIds={allExcludedSerials}
          onClose={() => setActiveSerialProduct(null)}
          onSave={(productId, selectedSerials) =>
            handleSaveSerials(activeSerialProduct.lineIndex, selectedSerials)
          }
        />
      )}

      <div className="relative flex h-full max-h-[95vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* ── HEADER ── */}
        <div className="flex flex-shrink-0 items-center justify-between border-b bg-indigo-50 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-lg text-white">
              <FaTruck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                New Delivery Order
              </h2>
              <p className="text-sm text-gray-500">
                Deliver full or partial quantities from an Approved Sales Order
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-red-500 transition hover:bg-red-50 rounded-full"
          >
            <FaTimes className="h-6 w-6" />
          </button>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white bg-opacity-60">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        )}

        {/* ── BODY ── */}
        <div className="flex-grow overflow-y-auto p-6 space-y-6 bg-white">
          {/* SECTION 1: Header fields */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className={labelStyles}>Delivery Date *</label>
              <input
                id="deliveryDate"
                type="datetime-local"
                value={formData.deliveryDate}
                onChange={handleHeaderChange}
                className={inputStyles}
              />
            </div>
            <div className="z-10 relative hidden">
              <label className={labelStyles}>Sales Order *</label>
              <SearchableDropdown
                options={soDropdownOptions}
                labelKey="displayLabel"
                valueKey="id"
                placeholder="-- Select Approved SO --"
                value={
                  formData.salesOrderId ? parseInt(formData.salesOrderId) : ""
                }
                onChange={handleSOSelect}
              />
            </div>
            <div className="z-10 relative">
              <label htmlFor="vendorId" className={labelStyles}>
                Customer *
              </label>
              <SearchableDropdown
                options={customers}
                labelKey="displayLabel"
                valueKey="id"
                placeholder="-- Select Customer --"
                value={
                  formData.customerId
                    ? parseInt(formData.customerId)
                    : ""
                }
                onChange={handleCustomerSelect}
              />
            </div>
            <div>
              <label className={labelStyles}>Prepared By</label>
              <input
                type="text"
                value={formData.preparedBy}
                disabled
                className="bg-gray-100 cursor-not-allowed block w-full p-2.5 border border-gray-300 rounded-lg text-sm text-gray-600"
              />
            </div>
          </div>

          <div>
            <label className={labelStyles}>Notes</label>
            <textarea
              id="notes"
              rows={2}
              className={`${inputStyles} resize-none`}
              placeholder="Optional delivery notes..."
              value={formData.notes}
              onChange={handleHeaderChange}
            />
          </div>

          {/* SO Management Container */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-6">
            <div className="flex items-center gap-2 mb-4 text-slate-800">
              <FaLayerGroup className="text-indigo-500" />
              <h3 className="font-bold">Sales Order Selection</h3>
            </div>

            {/* Input Row */}
            <div className="flex flex-col md:flex-row gap-3 items-end bg-white p-4 rounded-lg shadow-sm border border-slate-100">
              <div className="flex-1 w-full relative">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 ml-1">
                  Available Sales Orders
                </label>
                <SearchableDropdown
                  options={salesOrdersByCustomer}
                  labelKey="displayLabel"
                  valueKey="id"
                  placeholder={salesOrdersByCustomer.length === 0 ? "No SOs available" : "-- Select Pending SO --"}
                  value={selectedSO?.id}
                  onChange={handleSOSelect}
                />
              </div>
              
              <button
                type="button"
                onClick={handleAddSO}
                disabled={isLoading || !selectedSO}
                className="h-[42px] px-6 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-lg transition-all flex items-center gap-2 shadow-lg shadow-indigo-100 disabled:shadow-none whitespace-nowrap"
              >
                <FaPlus /> Add to Order
              </button>
            </div>

            {/* Selected SOs Display */}
            <div className="mt-6">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                SOs included in this receipt: {formData.deliveryOrderMainDetailed?.length}
              </h4>
              
              {formData.deliveryOrderMainDetailed?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {formData.deliveryOrderMainDetailed?.map((item) => (
                    <div 
                      key={item.salesOrderId}
                      className="flex items-center justify-between bg-white border border-indigo-100 rounded-lg p-3 shadow-sm hover:border-indigo-300 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 p-2 rounded text-indigo-600">
                          <FaFileInvoice />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 font-medium">SO Number</p>
                          <p className="text-sm font-bold text-slate-800">
                            {item.salesOrderNumber || `ID: ${item.salesOrderId}`}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => handleRemoveSO(item.salesOrderId)}
                        className="text-slate-300 hover:text-red-500 p-2 rounded-md hover:bg-red-50 transition-all"
                        title="Remove PO"
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-200 rounded-xl py-8 flex flex-col items-center justify-center">
                  <p className="text-slate-400 text-sm">No Sales Orders selected yet.</p>
                </div>
              )}
            </div>
          </div>

          <div className={"hidden"}>
            {/* SECTION 2: SO info banner */}
            {selectedSO && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <FaInfoCircle className="text-blue-500 mt-1 shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 text-sm">
                    Sales Order Details
                  </h4>
                  <div className="flex flex-wrap gap-4 mt-1">
                    <p className="text-xs text-blue-700">
                      Customer:{" "}
                      <span className="font-bold">{selectedSO.customerName}</span>
                    </p>
                    {selectedSO.locationName && (
                      <p className="text-xs text-blue-700">
                        Branch:{" "}
                        <span className="font-bold">
                          {selectedSO.locationName}
                        </span>
                      </p>
                    )}
                    <p className="text-xs text-blue-700">
                      SO Total:{" "}
                      <span className="font-bold">
                        {formatCurrency(selectedSO.totalAmount)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Stock shortage alert banner ── */}
            {stockShortageCount > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <FaExclamationTriangle className="text-red-500 shrink-0" />
                <p className="text-xs text-red-700 font-medium">
                  <span className="font-bold">{stockShortageCount} item(s)</span>{" "}
                  have no available stock at{" "}
                  <span className="font-bold">
                    {selectedSO?.locationName ?? "this branch"}
                  </span>{" "}
                  and cannot be delivered now. Stock must arrive first.
                </p>
              </div>
            )}

            {/* SECTION 3: Line items table */}
            {selectedSO && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-gray-700">
                    Delivery Lines ({formData.deliveryLines.length} items)
                  </h3>
                  <button
                    type="button"
                    onClick={handleSetMaxAll}
                    className="text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition"
                  >
                    Deliver All Available
                  </button>
                </div>

                <div className="border rounded-xl overflow-hidden shadow-sm">
                  <div className="max-h-[380px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase w-[28%]">
                            Product
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase w-20">
                            Ordered
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-orange-600 uppercase w-20">
                            Remaining
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-emerald-700 uppercase w-24">
                            In Stock
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-indigo-700 uppercase w-28">
                            Deliver Qty
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase w-44">
                            Serials
                          </th>
                          <th className="px-3 py-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {formData.deliveryLines.map((line, index) => {
                          const isFullyDelivered = line.remainingQuantity <= 0;
                          const isOutOfStock =
                            line.remainingQuantity > 0 &&
                            line.availableStock === 0;
                          const isStockBottleneck =
                            line.availableStock < line.remainingQuantity &&
                            line.availableStock > 0;
                          const pickedCount = line.serialNumbers.length;
                          const isFullyPicked =
                            pickedCount === line.quantityToDeliver &&
                            line.quantityToDeliver > 0;

                          return (
                            <tr
                              key={index}
                              className={
                                isFullyDelivered
                                  ? "bg-gray-50 opacity-50 pointer-events-none"
                                  : isOutOfStock
                                    ? "bg-red-50/40 opacity-70 pointer-events-none"
                                    : line.quantityToDeliver > 0
                                      ? "bg-indigo-50/40"
                                      : "hover:bg-gray-50"
                              }
                            >
                              {/* Product */}
                              <td className="px-5 py-3">
                                <p className="font-semibold text-gray-900 text-sm">
                                  {line.productName}
                                </p>
                                {isFullyDelivered && (
                                  <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 flex items-center gap-1 w-fit mt-1">
                                    <FaCheckDouble size={9} /> Fully Delivered
                                  </span>
                                )}
                                {isOutOfStock && (
                                  <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-1 w-fit mt-1">
                                    <FaExclamationTriangle size={9} /> No Stock
                                  </span>
                                )}
                                {isStockBottleneck && (
                                  <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1 w-fit mt-1">
                                    <FaWarehouse size={9} /> Low Stock
                                  </span>
                                )}
                              </td>

                              {/* Ordered */}
                              <td className="px-4 py-3 text-center">
                                <span className="font-bold text-gray-700 text-sm">
                                  {line.orderedQuantity}
                                </span>
                              </td>

                              {/* Remaining */}
                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`font-bold text-sm ${
                                    isFullyDelivered
                                      ? "text-gray-400"
                                      : "text-orange-600"
                                  }`}
                                >
                                  {line.remainingQuantity}
                                </span>
                              </td>

                              {/* In Stock */}
                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`inline-flex items-center gap-1 font-bold text-sm px-2 py-0.5 rounded-full ${
                                    isOutOfStock
                                      ? "text-red-600 bg-red-100"
                                      : isStockBottleneck
                                        ? "text-amber-700 bg-amber-100"
                                        : "text-emerald-700 bg-emerald-100"
                                  }`}
                                >
                                  <FaWarehouse size={9} />
                                  {line.availableStock}
                                </span>
                              </td>

                              {/* Deliver Qty */}
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  max={line.canDeliver}
                                  step="1"
                                  disabled={isFullyDelivered || isOutOfStock}
                                  value={line.quantityToDeliver}
                                  onChange={(e) =>
                                    handleQuantityChange(index, e.target.value)
                                  }
                                  className={`w-20 text-center rounded-lg border py-1.5 font-bold text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none transition ${
                                    isFullyDelivered || isOutOfStock
                                      ? "bg-gray-200 cursor-not-allowed text-gray-400"
                                      : line.quantityToDeliver > 0
                                        ? "border-indigo-400 text-indigo-700"
                                        : "border-gray-300 text-gray-700"
                                  }`}
                                />
                              </td>

                              {/* Serial Picker */}
                              <td className="px-4 py-3 text-center">
                                {line.hasSerial ? (
                                  line.quantityToDeliver > 0 ? (
                                    <button
                                      onClick={() =>
                                        setActiveSerialProduct({
                                          lineIndex: index,
                                          productId: line.productId,
                                          productName: line.productName,
                                          locationId: selectedSO.locationId,
                                          quantity: line.quantityToDeliver,
                                          conversionRate: 1,
                                        })
                                      }
                                      className={`flex w-full items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] uppercase font-bold transition-all ${
                                        isFullyPicked
                                          ? "bg-green-100 text-green-700 border border-green-200"
                                          : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 shadow-sm"
                                      }`}
                                    >
                                      {isFullyPicked ? (
                                        <>
                                          <FaCheckCircle /> Picked ({pickedCount})
                                        </>
                                      ) : (
                                        <>
                                          <FaQrcode /> Pick ({pickedCount}/
                                          {line.quantityToDeliver})
                                        </>
                                      )}
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">
                                      Set qty first
                                    </span>
                                  )
                                ) : (
                                  <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-1 rounded">
                                    Auto
                                  </span>
                                )}
                              </td>

                              {/* Clear button */}
                              <td className="px-3 py-3 text-center">
                                {line.quantityToDeliver > 0 &&
                                  !isFullyDelivered && (
                                    <button
                                      type="button"
                                      onClick={() => handleRevertLine(index)}
                                      className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition"
                                      title="Clear"
                                    >
                                      <FaTrash size={13} />
                                    </button>
                                  )}
                              </td>
                            </tr>
                          );
                        })}

                        {formData.deliveryLines.length === 0 && (
                          <tr>
                            <td
                              colSpan="7"
                              className="px-6 py-10 text-center text-gray-400 italic"
                            >
                              Select a Sales Order to see its items.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 4: Summary footer */}
            {selectedSO && totalUnitsToDeliver > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <FaCheckCircle className="text-green-500 shrink-0" />
                  <p className="text-sm text-gray-700">
                    Delivering{" "}
                    <span className="font-bold text-indigo-700">
                      {totalUnitsToDeliver} unit(s)
                    </span>{" "}
                    across{" "}
                    <span className="font-bold">
                      {
                        formData.deliveryLines.filter(
                          (l) => l.quantityToDeliver > 0,
                        ).length
                      }{" "}
                      line(s)
                    </span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Estimated Value</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(estimatedTotal)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 2: LINE ITEMS TABLE */}
          {formData.deliveryOrderMainDetailed?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-700">
                  Delivery Lines (Total {formData.deliveryOrderMainDetailed?.reduce((acc, so) => acc + (so?.deliveryOrderMainDetailedItem?.length || 0), 0)} items)
                </h3>
                <button
                  type="button"
                  onClick={handleSetMaxAll}
                  className="text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition"
                >
                  Deliver All Available
                </button>
              </div>

              <div className="border rounded-xl overflow-hidden shadow-sm h-[400px] overflow-y-auto bg-white">
                <table className="min-w-full divide-y divide-gray-200 relative border-separate border-spacing-0">
                  <thead className="bg-gray-100 sticky top-0 z-20">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 uppercase w-[28%]">Product</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase w-20">Ordered</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-orange-600 uppercase w-20">Remaining</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-emerald-700 uppercase w-24">In Stock</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-indigo-700 uppercase w-28">Deliver Qty</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase w-44">Serials</th>
                      <th className="px-3 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {formData.deliveryOrderMainDetailed.flatMap((so, soIndex) =>
                      so.deliveryOrderMainDetailedItem?.map((line, lineIndex) => {
                        // Mapping properties from your JSON
                        const remainingQty = line.remainingQuantity || 0;
                        const orderedQty = line.orderedQuantity || 0;
                        const availableStock = line.availableStock || 0; // Ensure this is provided by your state/API
                        const deliverQty = line.quantityToDeliver || 0;

                        const isFullyDelivered = remainingQty <= 0;
                        const isOutOfStock = remainingQty > 0 && availableStock === 0;
                        const isStockBottleneck = availableStock < remainingQty && availableStock > 0;
                        
                        const pickedCount = line.serialNumbers?.length || 0;
                        const isFullyPicked = line.hasSerial && pickedCount === deliverQty && deliverQty > 0;

                        const isGoodsItem = !serviceCategories.includes(line.categoryId)

                        return (
                          <tr
                            key={`${so.salesOrderNumber}-${line.id}-${lineIndex}`}
                            className={
                              isFullyDelivered
                                ? "bg-gray-50 opacity-50 pointer-events-none"
                                : isOutOfStock
                                ? "bg-red-50/40"
                                : deliverQty > 0
                                ? "bg-indigo-50/40"
                                : "hover:bg-gray-50 transition-colors"
                            }
                          >
                            {/* Product Column */}
                            <td className="px-5 py-3">
                              <div className="flex flex-col">
                                <span className="text-[11px] font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded shadow-sm" title="Sales Order Number">
                                  {so.salesOrderNumber}
                                </span>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-gray-900 text-sm">{line.productName}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-gray-400 text-[8px]">{line.brandName}</p>
                                </div>
                                
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {isFullyDelivered && (
                                    <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 flex items-center gap-1">
                                      <FaCheckDouble size={9} /> Fully Delivered
                                    </span>
                                  )}
                                  {isGoodsItem && isOutOfStock && (
                                    <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-1">
                                      <FaExclamationTriangle size={9} /> No Stock
                                    </span>
                                  )}
                                  {isStockBottleneck && (
                                    <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1">
                                      <FaWarehouse size={9} /> Low Stock
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Ordered */}
                            <td className="px-4 py-3 text-center">
                              <span className="font-bold text-gray-700 text-sm">
                                {orderedQty}
                              </span>
                            </td>

                            {/* Remaining */}
                            <td className="px-4 py-3 text-center">
                              <span className={`font-bold text-sm ${isFullyDelivered ? "text-gray-400" : "text-orange-600"}`}>
                                {remainingQty}
                              </span>
                            </td>

                            {/* In Stock */}
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex items-center gap-1 font-bold text-sm px-2 py-0.5 rounded-full ${
                                  isOutOfStock
                                    ? "text-red-600 bg-red-100"
                                    : isStockBottleneck
                                    ? "text-amber-700 bg-amber-100"
                                    : "text-emerald-700 bg-emerald-100"
                                }`}
                              >
                                <FaWarehouse size={9} />
                                {isGoodsItem ? availableStock : "-"}
                              </span>
                            </td>

                            {/* Deliver Qty Input */}
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                max={Math.min(remainingQty, availableStock)}
                                disabled={isFullyDelivered || isOutOfStock || !isGoodsItem}
                                value={isGoodsItem ? deliverQty : orderedQty}
                                onChange={(e) => handleQuantityChange(soIndex, lineIndex, e.target.value)}
                                className={`w-20 text-center rounded-lg border py-1.5 font-bold text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none transition ${
                                  isFullyDelivered || isOutOfStock
                                    ? "bg-gray-200 cursor-not-allowed text-gray-400"
                                    : deliverQty > 0
                                    ? "border-indigo-400 text-indigo-700 shadow-sm"
                                    : "border-gray-300 text-gray-700"
                                }`}
                              />
                            </td>

                            {/* Serial Picker */}
                            <td className="px-4 py-3 text-center">
                              {line.hasSerial ? (
                                deliverQty > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => setActiveSerialProduct({
                                      soIndex,
                                      lineIndex,
                                      productId: line.productId,
                                      productName: line.productName,
                                      quantity: deliverQty
                                    })}
                                    className={`flex w-full items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] uppercase font-bold transition-all ${
                                      isFullyPicked
                                        ? "bg-green-100 text-green-700 border border-green-200"
                                        : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 shadow-sm"
                                    }`}
                                  >
                                    {isFullyPicked ? (
                                      <>
                                        <FaCheckCircle /> Picked ({pickedCount})
                                      </>
                                    ) : (
                                      <>
                                        <FaQrcode /> Pick ({pickedCount}/{deliverQty})
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Set qty first</span>
                                )
                              ) : (
                                <span className="text-xs text-gray-400 font-medium bg-gray-100 px-2 py-1 rounded">Auto (No Serial)</span>
                              )}
                            </td>

                            {/* Clear Button */}
                            <td className="px-3 py-3 text-center">
                              {deliverQty > 0 && !isFullyDelivered && (
                                <button
                                  type="button"
                                  onClick={() => handleRevertLine(soIndex, lineIndex)}
                                  className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition"
                                  title="Clear quantity"
                                >
                                  <FaTrash size={13} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex-shrink-0 border-t bg-gray-50 px-6 py-4 flex justify-between items-center">
          <p className="text-xs text-gray-400 italic">
            {hasPartialLines
              ? "⚠ Some lines are partially fulfilled. Remaining can be delivered later."
              : ""}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || formData.deliveryOrderMainDetailed?.length <= 0}
              className="px-5 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition shadow-md disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              <FaTruck /> Confirm Delivery
            </button>
          </div>
        </div>
      </div>
      <AverageCostModal 
        isOpen={showAverageCostConfig}
        items={itemsWithoutAverageCost}
        onClose={closeAverageCostConfig}
        onSave={handleSaveCostsAndContinue}
      />
    </div>
  );
};

export default AddDeliveryOrder;
